import { WWW_URLS } from '../../../backendServices/urls';
import   React         ,
       { useCallback    ,
         useEffect      ,
         useMemo        ,
         useRef         ,
         useState       }  from 'react';

import   classNames       from 'classnames';

import { ExternalLink  ,
         FlaskConical  ,
         Film          ,
         Mail          ,
         Radio         ,
         RefreshCw     ,
         RotateCcw     }  from 'lucide-react';

import   styles           from './ReportModeration.module.scss';

import { useTypedSelector } from '../../../store/useTypeSelector';
import { RootState        } from '../../../store/generalReducer';

import { ReportItem            ,
         ReportList            ,
         ReportStatus          ,
         REPORT_REASON_LABELS  ,
         REPORT_STATUS_LABELS  ,
         URGENT_REASONS        } from '../../../dataTypes/moderationManagment/report_types';

import { decideReports        ,
         fetchReports         ,
         ReportModerationError } from '../../../backendServices/moderationManagment/api_report';

import { playReportChime      } from '../chatModerationManagment/chatModerationUi';

/**
 * Onglet « Signalements » de l'écran Modération — super-admins seulement.
 *
 * Les spectateurs signalent une vidéo ou une chaîne en direct depuis le lecteur
 * du portail (bouton « Signaler », huit motifs et un commentaire). Jusqu'ici,
 * ces signalements étaient enregistrés et personne ne les voyait. Le DSA
 * (art. 16-17) demande de traiter chacun avec diligence et d'en informer
 * l'auteur : on les lit ici, on ouvre le contenu, on le retire avec les outils
 * habituels si besoin, puis on clôt le signalement avec une note. La décision
 * part alors d'elle-même par e-mail à l'auteur, note comprise (case « Prévenir
 * l'auteur par e-mail », cochée par défaut).
 *
 * En temps réel (2026-10-08) : la liste se relit toutes les 5 secondes, 20
 * quand l'onglet du navigateur est caché, sans clignoter. Un nouveau
 * signalement s'annonce par son propre son (playReportChime, distinct du chat), une bannière et une carte marquée
 * « Nouveau » pendant une minute ; le nombre à traiter suit dans le titre de
 * l'onglet Modération, visible depuis tout le reste de la régie. Pas de
 * « push » : admin-api n'en a pas, et une requête toutes les 5 secondes d'un
 * super-admin ne pèse rien.
 *
 * Démonstration sans serveur : `?demoSignalements` dans l'adresse (un
 * signalement fictif arrive au bout de 15 secondes).
 */

type Filter = ReportStatus | 'all';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'open'      , label: 'À traiter'         },
  { key: 'resolved'  , label: 'Contenu retiré'    },
  { key: 'dismissed' , label: 'Classés sans suite' },
  { key: 'all'       , label: 'Tous'              },
];

const PORTAL_URL        = WWW_URLS.portal_front;
const REFRESH_MS        = 5_000;
const REFRESH_HIDDEN_MS = 20_000;
/** Combien de temps une carte reste marquée « Nouveau ». */
const FRESH_MS          = 60_000;

interface Props {
  /** Le nombre de signalements à traiter, pour le titre de l'onglet. */
  onOpenCount? : (count: number) => void;
}

const ReportModerationList: React.FC<Props> = ({ onOpenCount }) => {

  const { authentication } = useTypedSelector((state: RootState) => state.authenticationReducer);
  const adminId = authentication.admin?.id ?? null;
  const demo    = isDemo();

  const [ filter  , setFilter  ] = useState<Filter>('open');
  const [ list    , setList    ] = useState<ReportList>({ reports: [], counts: { open: 0, resolved: 0, dismissed: 0 } });
  const [ loading , setLoading ] = useState(false);
  const [ error   , setError   ] = useState('');
  const [ busyKey , setBusyKey ] = useState<string | null>(null);
  const [ notice  , setNotice  ] = useState('');
  /** Signalements arrivés pendant qu'on regardait : clé → fin du marquage. */
  const [ fresh   , setFresh   ] = useState<Map<string, number>>(new Map());
  /** Bannière « N nouveaux signalements », jusqu'à ce qu'on la ferme. */
  const [ arrived , setArrived ] = useState(0);
  const [ updated , setUpdated ] = useState<Date | null>(null);

  // Ce qu'on a déjà vu, pour reconnaître ce qui arrive. Le premier relevé d'un
  // filtre ne compte pas comme une arrivée : changer de filtre fait apparaître
  // des signalements anciens, pas nouveaux.
  const seenKeys     = useRef<Set<string>>(new Set());
  const primedFilter = useRef<Filter | null>(null);
  const lastOpen     = useRef<number | null>(null);

  /** `manual` : le bouton Rafraîchir ou une décision — eux seuls font tourner
   *  l'icône ; le relevé automatique reste silencieux. */
  const load = useCallback(async (manual = false) => {
    if (!demo && adminId === null) return;
    if (manual) setLoading(true);
    try {
      const fetched = demo ? demoList(filter) : await fetchReports(adminId as number, filter);
      const primed  = primedFilter.current === filter;

      // Les nouvelles cartes : jamais vues, ouvertes, et pas le premier relevé du filtre.
      const now  = Date.now();
      const news = fetched.reports
        .map(r => `${r.kind}:${r.id}`)
        .filter(key => !seenKeys.current.has(key));
      fetched.reports.forEach(r => seenKeys.current.add(`${r.kind}:${r.id}`));
      const freshKeys = primed
        ? fetched.reports.filter(r => r.status === 'open' && news.includes(`${r.kind}:${r.id}`)).map(r => `${r.kind}:${r.id}`)
        : [];
      if (freshKeys.length > 0) {
        setFresh(previous => {
          const next = new Map(previous);
          freshKeys.forEach(key => next.set(key, now + FRESH_MS));
          return next;
        });
      }

      // Le compteur « à traiter » dit les arrivées quel que soit le filtre affiché.
      const grew = lastOpen.current !== null ? fetched.counts.open - lastOpen.current : 0;
      if (grew > 0) {
        setArrived(previous => previous + grew);
        playReportChime();
      }
      lastOpen.current     = fetched.counts.open;
      primedFilter.current = filter;

      setList(fetched);
      setUpdated(new Date());
      setError('');
    } catch (e) {
      setError(explain(e));
    } finally {
      if (manual) setLoading(false);
    }
  }, [adminId, filter, demo]);

  // Relevé en continu : un délai qui se reprogramme (et non un intervalle),
  // pour ne jamais lancer une requête avant que la précédente ait répondu.
  useEffect(() => {
    let stopped = false;
    let timer: number | undefined;
    const tick = async () => {
      await load();
      if (!stopped) timer = window.setTimeout(tick, document.hidden ? REFRESH_HIDDEN_MS : REFRESH_MS);
    };
    tick();
    // Revenir sur l'onglet du navigateur relit tout de suite.
    const onVisible = () => { if (!document.hidden) { window.clearTimeout(timer); tick(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  // Les marques « Nouveau » s'effacent d'elles-mêmes.
  useEffect(() => {
    if (fresh.size === 0) return;
    const next = Math.min(...Array.from(fresh.values()));
    const timer = window.setTimeout(() => {
      const now = Date.now();
      setFresh(previous => new Map(Array.from(previous).filter(([, until]) => until > now)));
    }, Math.max(0, next - Date.now()) + 50);
    return () => window.clearTimeout(timer);
  }, [fresh]);

  useEffect(() => { onOpenCount?.(list.counts.open); }, [list.counts.open, onOpenCount]);

  const decide = async (report: ReportItem, status: ReportStatus, note: string, wholeTarget: boolean, notify: boolean) => {
    // « Tous les signalements de ce contenu » : la même décision vaut pour les
    // autres signalements ouverts qui visent la même vidéo ou la même chaîne.
    const items = wholeTarget
      ? list.reports.filter(r => r.kind === report.kind && r.target.id === report.target.id && r.status === 'open')
      : [ report ];
    const key = `${report.kind}:${report.id}`;
    setBusyKey(key);
    setNotice('');
    try {
      let notified = 0;
      if (!demo && adminId !== null) {
        ({ notified } = await decideReports(adminId, items.map(r => ({ kind: r.kind, id: r.id })), status, note, notify));
      }
      await load(true);
      if (demo) {
        // En démonstration, la décision ne part nulle part : on la montre.
        notified = notify && status !== 'open' ? new Set(items.map(r => r.reporter.email).filter(Boolean)).size : 0;
        setList(previous => applyDemoDecision(previous, items, status, note, filter, notify));
      }
      if (status !== 'open') {
        setNotice(notify
          ? notified > 0
            ? `Décision envoyée par e-mail à ${notified === 1 ? "l'auteur du signalement" : `${notified} auteurs de signalements`}.`
            : "Décision enregistrée, mais aucun e-mail n'a pu partir (adresse absente ou envoi impossible)."
          : "Décision enregistrée, sans e-mail à l'auteur.");
      }
    } catch (e) {
      setError(explain(e));
    } finally {
      setBusyKey(null);
    }
  };

  // Les motifs graves d'abord, puis les contenus les plus signalés, puis les plus récents.
  const reports = useMemo(() => [...list.reports].sort((a, b) =>
      Number(URGENT_REASONS.has(b.reasonCode)) - Number(URGENT_REASONS.has(a.reasonCode))
   || b.target.openReports - a.target.openReports
   || (b.creationDate ?? '').localeCompare(a.creationDate ?? '')
  ), [list.reports]);

  return (
    <div className={styles.wrap}>

      {demo && (
        <p className={styles.demo}>
          <FlaskConical size={17} /> Démonstration : signalements fictifs, rien n'est envoyé au serveur.
          Retirez « ?demoSignalements » de l'adresse pour revenir aux vrais.
        </p>
      )}

      <div className={styles.toolbar}>
        <div className={styles.filters} role="tablist" aria-label="Statut des signalements">
          {FILTERS.map(f => (
            <button key={f.key} type="button" role="tab" aria-selected={filter === f.key}
                    className={classNames(styles.filter, filter === f.key && styles.filterOn)}
                    onClick={() => setFilter(f.key)}>
              {f.label}
              {f.key !== 'all' && <span className={styles.filterCount}>{list.counts[f.key]}</span>}
            </button>
          ))}
        </div>
        <span className={styles.spacer} />
        <span className={styles.live} title="La liste se met à jour toute seule toutes les 5 secondes">
          <span className={classNames(styles.liveDot, error !== '' && styles.liveDotOff)} />
          {error !== '' ? 'Hors ligne' : updated ? `En direct · ${updated.toLocaleTimeString('fr-FR')}` : 'En direct'}
        </span>
        <button type="button" className={styles.btnGhost} onClick={() => load(true)} disabled={loading}>
          <RefreshCw size={15} className={classNames(loading && styles.spin)} /> Rafraîchir
        </button>
      </div>

      <p className={styles.hint}>
        Un signalement se traite avec diligence (DSA, art. 16) : ouvrez le contenu, retirez-le avec ses outils
        habituels s'il enfreint la loi ou les règles de la communauté, puis clôturez le signalement. Son auteur
        reçoit alors la décision par e-mail, avec votre précision s'il y en a une ; son identité n'est jamais
        communiquée au créateur.
      </p>

      {arrived > 0 && (
        <p className={styles.arrived}>
          {arrived === 1 ? 'Un nouveau signalement est arrivé.' : `${arrived} nouveaux signalements sont arrivés.`}
          {filter !== 'open' && (
            <button type="button" className={styles.btnGhost} onClick={() => { setFilter('open'); setArrived(0); }}>
              Voir les signalements à traiter
            </button>
          )}
          <span className={styles.spacer} />
          <button type="button" className={styles.btnGhost} onClick={() => setArrived(0)}>Compris</button>
        </p>
      )}
      {error  !== '' && <p className={styles.error}>{error}</p>}
      {notice !== '' && <p className={styles.notice}>{notice}</p>}

      {reports.length === 0 && error === '' && (
        <p className={styles.empty}>
          {loading ? 'Chargement…' : filter === 'open' ? 'Aucun signalement à traiter.' : 'Aucun signalement.'}
        </p>
      )}

      <ul className={styles.list}>
        {reports.map(report => (
          <ReportCard key={`${report.kind}:${report.id}`} report={report}
                      fresh={fresh.has(`${report.kind}:${report.id}`)}
                      busy={busyKey === `${report.kind}:${report.id}`}
                      onDecide={(status, note, whole, notify) => decide(report, status, note, whole, notify)} />
        ))}
      </ul>
    </div>
  );
};

// ---- Une carte de signalement ----

const ReportCard: React.FC<{
  report   : ReportItem;
  /** Arrivé pendant qu'on regardait : marqué « Nouveau » une minute. */
  fresh    : boolean;
  busy     : boolean;
  onDecide : (status: ReportStatus, note: string, wholeTarget: boolean, notify: boolean) => void;
}> = ({ report, fresh, busy, onDecide }) => {

  const [ note   , setNote   ] = useState('');
  const [ whole  , setWhole  ] = useState(report.target.openReports > 1);
  const [ notify , setNotify ] = useState(report.reporter.email !== null);

  const urgent = URGENT_REASONS.has(report.reasonCode);
  const KindIcon = report.kind === 'video' ? Film : Radio;
  const link = report.target.slug ? `${PORTAL_URL}/${report.target.slug}` : null;

  return (
    <li className={classNames(styles.card, urgent && report.status === 'open' && styles.cardUrgent, fresh && styles.cardFresh)}>

      <div className={styles.cardHead}>
        {fresh && <span className={styles.tagFresh}>Nouveau</span>}
        <span className={styles.kind}>
          <KindIcon size={15} /> {report.kind === 'video' ? 'Vidéo' : 'Chaîne'}
        </span>
        <strong className={styles.title}>{report.target.title || 'Contenu introuvable'}</strong>
        {link && (
          <a className={styles.link} href={link} target="_blank" rel="noreferrer">
            Ouvrir <ExternalLink size={13} />
          </a>
        )}
        <span className={styles.spacer} />
        {report.target.deleted && <span className={styles.tagMuted}>supprimé</span>}
        {!report.target.deleted && !report.target.visible && <span className={styles.tagMuted}>masqué</span>}
        {report.target.openReports > 1 && (
          <span className={styles.tagAlert}>{report.target.openReports} signalements ouverts</span>
        )}
      </div>

      <p className={styles.meta}>
        {report.target.creator ? <>Créateur : {report.target.creator} · </> : null}
        Signalé le {formatDate(report.creationDate)}
      </p>

      <p className={classNames(styles.reason, urgent && styles.reasonUrgent)}>
        {REPORT_REASON_LABELS[report.reasonCode] ?? report.reasonCode}
      </p>
      {report.text && <blockquote className={styles.quote}>{report.text}</blockquote>}

      <p className={styles.meta}>
        Auteur :{' '}
        {report.reporter.email
          ? <a className={styles.link} href={`mailto:${report.reporter.email}`}><Mail size={13} /> {report.reporter.email}</a>
          : 'compte supprimé'}
        {report.reporter.ip && <> · IP {report.reporter.ip}</>}
      </p>

      {report.status === 'open' ? (
        <div className={styles.decide}>
          <textarea className={styles.note} rows={2} maxLength={2000} value={note}
                    placeholder="Précision pour l'auteur du signalement (facultatif) : ajoutée à l'e-mail qui lui annonce la décision"
                    onChange={e => setNote(e.target.value)} />
          <div className={styles.actions}>
            {report.target.openReports > 1 && (
              <label className={styles.whole}>
                <input type="checkbox" checked={whole} onChange={e => setWhole(e.target.checked)} />
                Appliquer aux {report.target.openReports} signalements de ce contenu
              </label>
            )}
            <label className={styles.whole}>
              <input type="checkbox" checked={notify} disabled={report.reporter.email === null}
                     onChange={e => setNotify(e.target.checked)} />
              Prévenir l'auteur par e-mail
            </label>
            <span className={styles.spacer} />
            <button type="button" className={styles.btnDismiss} disabled={busy}
                    onClick={() => onDecide('dismissed', note, whole, notify)}>
              Classer sans suite
            </button>
            <button type="button" className={styles.btnResolve} disabled={busy}
                    onClick={() => onDecide('resolved', note, whole, notify)}>
              Contenu retiré
            </button>
          </div>
        </div>
      ) : (
        <div className={styles.decision}>
          <span className={report.status === 'resolved' ? styles.tagResolved : styles.tagMuted}>
            {REPORT_STATUS_LABELS[report.status]}
          </span>
          <span className={styles.meta}>
            {report.decidedBy ? `par ${report.decidedBy}, ` : ''}le {formatDate(report.decisionDate)}
          </span>
          {report.decisionNote && <span className={styles.decisionNote}>« {report.decisionNote} »</span>}
          <span className={styles.meta}>
            {report.notificationDate ? `· Auteur prévenu le ${formatDate(report.notificationDate)}` : '· Auteur non prévenu'}
          </span>
          <span className={styles.spacer} />
          <button type="button" className={styles.btnGhost} disabled={busy}
                  onClick={() => onDecide('open', '', false, false)}>
            <RotateCcw size={14} /> Rouvrir
          </button>
        </div>
      )}
    </li>
  );
};

// ---- Aides ----

const formatDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' }) : '—';

const explain = (e: unknown): string => {
  if (e instanceof ReportModerationError) {
    if (e.status === 403) return "Réservé aux super-admins.";
    if (e.status === 404) return "Signalements indisponibles : admin-api n'est pas encore à jour sur le serveur (ReportModerationController).";
    return e.message;
  }
  return "Le serveur ne répond pas.";
};

function isDemo(): boolean {
  try { return new URLSearchParams(window.location.search).has('demoSignalements'); }
  catch { return false; }
}

// ---- Démonstration (?demoSignalements) ----

const DEMO: ReportItem[] = [
  {
    kind: 'channel', id: 1, reasonCode: 'hateful_or_abusive',
    text: 'Le présentateur insulte une communauté pendant le direct, vers 21 h 10.',
    creationDate: new Date(Date.now() - 25 * 60_000).toISOString(),
    reporter: { userId: 12, email: 'spectateur.un@example.com', ip: '91.170.x.x' },
    target: { id: 7, title: 'Soirée débat — direct', slug: 'soiree-debat', deleted: false, visible: true, creator: 'Camille Martin', openReports: 2 },
    status: 'open', decisionNote: null, decisionDate: null, decidedBy: null,
  },
  {
    kind: 'channel', id: 2, reasonCode: 'harassment', text: null,
    creationDate: new Date(Date.now() - 18 * 60_000).toISOString(),
    reporter: { userId: 31, email: 'lea.d@example.com', ip: '82.64.x.x' },
    target: { id: 7, title: 'Soirée débat — direct', slug: 'soiree-debat', deleted: false, visible: true, creator: 'Camille Martin', openReports: 2 },
    status: 'open', decisionNote: null, decisionDate: null, decidedBy: null,
  },
  {
    kind: 'video', id: 3, reasonCode: 'rights_infringement',
    text: "C'est ma musique, utilisée sans mon accord.",
    creationDate: new Date(Date.now() - 3 * 3600_000).toISOString(),
    reporter: { userId: 44, email: 'artiste@example.com', ip: '2a01:e0a:…' },
    target: { id: 120, title: 'Clip de lancement', slug: 'clip-de-lancement', deleted: false, visible: true, creator: 'Studio Nord', openReports: 1 },
    status: 'open', decisionNote: null, decisionDate: null, decidedBy: null,
  },
  {
    kind: 'video', id: 4, reasonCode: 'spam_or_scam', text: 'Lien vers une arnaque dans la description.',
    creationDate: new Date(Date.now() - 2 * 86400_000).toISOString(),
    reporter: { userId: 9, email: 'paul@example.com', ip: '78.192.x.x' },
    target: { id: 98, title: 'Gagnez 1000 € par jour', slug: 'gagnez', deleted: true, visible: false, creator: 'Compte test', openReports: 0 },
    status: 'resolved', decisionNote: 'Vidéo supprimée et compte averti.',
    decisionDate: new Date(Date.now() - 86400_000).toISOString(), decidedBy: 'Équipe Kleon',
    notificationDate: new Date(Date.now() - 86400_000).toISOString(),
  },
];

// Un signalement fictif arrive au bout de 15 secondes, pour voir l'annonce.
const DEMO_STARTED = Date.now();
let demoArrived = false;

function demoList(filter: Filter): ReportList {
  if (!demoArrived && Date.now() - DEMO_STARTED > 15_000) {
    demoArrived = true;
    DEMO.unshift({
      kind: 'channel', id: 5, reasonCode: 'violent_or_shocking',
      text: 'Images très violentes montrées à l\'antenne à l\'instant.',
      creationDate: new Date().toISOString(),
      reporter: { userId: 57, email: 'nadia@example.com', ip: '86.246.x.x' },
      target: { id: 11, title: 'Concert en plein air', slug: 'concert-plein-air', deleted: false, visible: true, creator: 'Studio Nord', openReports: 1 },
      status: 'open', decisionNote: null, decisionDate: null, decidedBy: null,
    });
  }
  return withCounts(DEMO, filter);
}

function withCounts(all: ReportItem[], filter: Filter): ReportList {
  const counts = { open: 0, resolved: 0, dismissed: 0 };
  all.forEach(r => { counts[r.status] += 1; });
  return { reports: filter === 'all' ? all : all.filter(r => r.status === filter), counts };
}

function applyDemoDecision(_previous: ReportList, items: ReportItem[], status: ReportStatus,
                           note: string, filter: Filter, notify: boolean): ReportList {
  const touched = new Set(items.map(r => `${r.kind}:${r.id}`));
  DEMO.forEach((r, i) => {
    if (!touched.has(`${r.kind}:${r.id}`)) return;
    DEMO[i] = { ...r, status, decisionNote: status === 'open' ? null : (note || null),
                decisionDate: status === 'open' ? null : new Date().toISOString(),
                decidedBy: status === 'open' ? null : 'Vous',
                notificationDate: status !== 'open' && notify && r.reporter.email ? new Date().toISOString() : null };
  });
  DEMO.forEach((r, i) => {
    const open = DEMO.filter(o => o.kind === r.kind && o.target.id === r.target.id && o.status === 'open').length;
    DEMO[i] = { ...DEMO[i], target: { ...DEMO[i].target, openReports: open } };
  });
  return withCounts(DEMO, filter);
}

export default ReportModerationList;
